import { Link, useParams } from 'react-router-dom';

import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import newKimScreenshot from '@/assets/img/new-kim.png';
import { BetaBadge } from '@/new/components/beta-badge';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent } from '@/new/components/shadcn/dialog';

interface NewKimDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subsetId: string;
}

export function NewKimDialog({ open, onOpenChange, subsetId }: NewKimDialogProps) {
  const { matterId } = useParams();

  const handleDismiss = () => {
    if (localStorage.getItem('isUsingNewKim') === null) {
      localStorage.setItem('isUsingNewKim', 'false');
      trackCustomEvent(TrackingEventsV2.KimDismissNewKimDialog);
    }
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleDismiss()}>
      <DialogContent className="flex max-w-5xl p-0" onOpenAutoFocus={(e) => e.preventDefault()}>
        <div className="relative flex basis-1/2 flex-col overflow-x-hidden">
          <div className="mt-18 ml-16">
            <BetaBadge className="mb-1 w-fit" />
            <h2 className="max-w-xs font-serif text-4xl font-medium">Try the new and improved Kim.</h2>

            <div className="mt-5.5 flex gap-2">
              <Button asChild size={'sm'}>
                <Link
                  to={`/matters/${matterId}/subsets/${subsetId}/new-kim`}
                  onClick={() => {
                    trackCustomEvent(TrackingEventsV2.KimEnableNewExperience, {
                      matterId,
                      subsetId,
                      from: 'New Kim dialog',
                    });
                    localStorage.setItem('isUsingNewKim', 'true');
                  }}
                >
                  Take me there
                  <Icon className="lucide--arrow-right" />
                </Link>
              </Button>
            </div>

            <div className="absolute -bottom-5 translate-x-10 scale-140 overflow-visible [perspective:3000px]">
              <img
                src={newKimScreenshot}
                alt="New Kim experience preview"
                className="[transform:rotateX(18deg)_rotateY(-20deg)_rotateZ(4deg)] mask-b-from-60%"
              />
            </div>
          </div>
        </div>
        <div className="basis-1/2 border-l py-36 pr-16 pl-14">
          <ul className="flex flex-col gap-6">
            <li className="flex items-start gap-3">
              <Icon className="lucide--messages-square mt-1 shrink-0 text-teal-500" />
              <div>
                <p className="mb-1 font-medium text-gray-700">Multi-turn conversations</p>
                <p className="mt-2 text-sm text-gray-500">
                  Ask follow-up questions to refine your search or adjust your answer's formatting.
                </p>
              </div>
            </li>

            <li className="flex items-start gap-3">
              <Icon className="lucide--search mt-0.75 shrink-0 text-teal-500" />
              <div>
                <p className="mb-1 font-medium text-gray-700">More intelligent, deeper research</p>
                <p className="mt-2 text-sm text-gray-500">
                  Kim now indexes every column in your Matter for richer ranking and retrieval, and iteratively refines
                  its searches to produce a thorough answer.
                </p>
              </div>
            </li>

            <li className="flex items-start gap-3">
              <Icon className="lucide--archive mt-0.75 shrink-0 text-teal-500" />
              <div>
                <p className="mb-1 font-medium text-gray-700">You'll still keep existing answers</p>
                <p className="mt-2 text-sm text-gray-500">
                  All previous Kim questions and answers remain fully accessible by reverting to the current Kim
                  experience. When the old experience is eventually removed, we'll migrate all existing answers over.
                </p>
              </div>
            </li>
          </ul>
        </div>
      </DialogContent>
    </Dialog>
  );
}
